import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import CompanyDashboardAddExistingKeyResultsPopup from "../../../company-dashboard-add-existing-keyresults-popup/company-dashboard-add-existing-keyresults-popup";
import addCirculIcon from "../../../../../assets/icons/addREC.svg";
import CloseIcon from "../../../../../assets/icons/closeIcon.svg";
import { updateInitiative } from "../../../../../api/initiative";
import DeleteIcon from "../../../../../assets/icons/linkBreak.svg";
import "./company-dashboard-initiative-drawer-side-bar-add-existing-key-results-popup.scss";
import WarningConfirmationPopup from "../../../../../common/components/warning-confirmation-popup/warning-confirmation-popup";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
export interface StatusOption {
  statusId: number;
  statusName: string;
}
interface KeyResultRow {
  id: number;
  keyResult: string | null;
  keyResultId?: number | null;
  weightage: string;
}
const CompanyDashboardAddExistingKeyResultsPopupMainComponent = (
  props: any,
) => {
  const {
    isOpenAddPopup,
    onCancel,
    existingKeyResultsData,
    initiativeAPIData,
    fetchInitiatives,
    setAlertType,
    setAlertOpen,
    setAlertMessage,
    setLoading,
    fetchInitiativeById,
    handleNewCreateKeyResult,
    listKeyResultsData,
  } = props;
  const [rows, setRows] = useState<KeyResultRow[]>([
    { id: Date.now(), keyResult: null, weightage: "" },
  ]);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);

  const [formError, setFormError] = useState<string | null>(null);
  const [isOpenAddKeyResultPopup, setIsOpenAddKeyResultPopup] = useState(false);
  const [deletedKeyResultIds, setDeletedKeyResultIds] = useState<number[]>([]);
  const [isDeleteWarningOpen, setIsDeleteWarningOpen] = useState(false);
  const [pendingDeleteRowId, setPendingDeleteRowId] = useState<number | null>(
    null,
  );

  useEffect(() => {
    if (!isOpenAddPopup) return;

    const mappedKeyResults =
      initiativeAPIData?.keyResults || listKeyResultsData || [];
    const bytes = new Uint32Array(1);
    crypto.getRandomValues(bytes);
    const formattedMappedRows =
      mappedKeyResults.map((kr: any) => ({
        id: Date.now() + crypto.randomUUID(), // unique id for row
        keyResult: kr.keyResultTitle || kr?.title || "",
        keyResultId: kr.keyResultId || kr?.id || null, // <--- IMPORTANT
        weightage: kr.weight || kr?.weightage || "",
      })) || [];

    // If there are no rows, show one empty row
    // If there are rows, don't add an empty row initially
    if (formattedMappedRows.length === 0) {
      setRows([
        { id: Date.now(), keyResult: null, keyResultId: null, weightage: "" },
      ]);
    } else {
      setRows(formattedMappedRows);
    }
  }, [initiativeAPIData, isOpenAddPopup]);
  const normalizedExistingKeyResultsData = existingKeyResultsData?.map(
    (kr: any) => ({
      ...kr,
      keyResultId: kr.id,
      keyResultTitle: kr.title,
    }),
  );
  const getAvailableKeyResults = (currentIndex: number) => {
    const selectedIdsInRows = rows
      .filter((_, idx) => idx !== currentIndex)
      .map((row) => row.keyResultId)
      .filter(Boolean);

    const mappedIds =
      initiativeAPIData?.keyResults
        .map((kr: any) => kr.keyResultId)
        .filter((id: number) => !deletedKeyResultIds.includes(id)) || []; // ✅ Exclude deleted ones

    const excludeIds = new Set([...selectedIdsInRows, ...mappedIds]);

    const response = normalizedExistingKeyResultsData.filter(
      (kr: any) => !excludeIds.has(kr.keyResultId),
    );

    return response;
  };

  const handleCreate = async () => {
    try {
      if (initiativeAPIData?.id) {
        setLoading(true);
        const addedOrUpdatedKeyResults = rows
          .filter((row) => row.keyResultId)
          .map((row) => ({
            objectiveId:
              existingKeyResultsData?.find(
                (key: any) => key?.id === row?.keyResultId,
              )?.objectiveId || [],
            keyResultId: row.keyResultId,
            isRemove: 0,
          }));

        // Deleted key results
        const removedKeyResults = deletedKeyResultIds.map((keyResultId) => ({
          objectiveId:
            existingKeyResultsData?.find((key: any) => key?.id === keyResultId)
              ?.objectiveId || [],
          keyResultId,
          isRemove: 1,
        }));

        const payload = {
          objectives: [...addedOrUpdatedKeyResults, ...removedKeyResults],
          threshold: initiativeAPIData.threshold || 10,
        };
        await updateInitiative(initiativeAPIData?.id, payload);
        fetchInitiativeById();
        fetchInitiatives();

        setAlertType("success");
        setAlertMessage("Key Results updated successfully!");
        setAlertOpen(true);
        setLoading(false);
        onCancel();
      }
    } catch {
      setAlertType("error");
      setAlertMessage("Failed to update Key Results.");
      setAlertOpen(true);
      setLoading(false);
    }
  };
  const validationSchema = Yup.object().shape({
    keyResults: Yup.array().of(
      Yup.object().shape({
        keyResult: Yup.string().required("Select a Key Result"),
        weightage: Yup.number()
          .typeError("Weightage must be a number")
          .min(0, "Weightage cannot be negative")
          .max(100, "Weightage cannot exceed 100"),
      }),
    ),
  });

  const handleAddRow = () => {
    // Check if there are any filled rows
    const hasAnyFilledRows = rows.some((r) => r.keyResultId);

    // Check if the last row is empty (no keyResultId and no keyResult)
    const lastRow = rows.at(-1);
    const isLastRowEmpty = !lastRow?.keyResultId && !lastRow?.keyResult;

    if (!hasAnyFilledRows && rows?.length > 0) {
      setFormError("Please fill the current row before adding a new one.");
      return;
    }

    // If there are filled rows but the last row is empty, show error
    if (isLastRowEmpty && rows?.length > 0) {
      setFormError("Please fill the current row before adding a new one.");
      return;
    }

    setFormError(null);
    setRows([
      ...rows,
      { id: Date.now(), keyResult: null, keyResultId: null, weightage: "" },
    ]);
  };
  const handleDeleteRow = (id: number) => {
    const rowToDelete = rows.find((row) => row.id === id);

    // Only consider rows that have a selected key result
    if (!rowToDelete?.keyResultId) {
      // Row is empty, just remove it without warning
      setRows(rows.filter((row) => row.id !== id));
      return;
    }

    // If only one filled key result exists, show warning
    const filledRowsCount = rows.filter((r) => r.keyResultId).length;
    if (filledRowsCount === 1) {
      setPendingDeleteRowId(id);
      setIsDeleteWarningOpen(true);
      return;
    }

    // Normal delete
    deleteRow(id);
  };

  const deleteRow = (id: number) => {
    const deletedRow = rows.find((row) => row.id === id);

    // Check and cast safely before updating state
    if (deletedRow && typeof deletedRow.keyResultId === "number") {
      setDeletedKeyResultIds((prev: number[]) => [
        ...prev,
        deletedRow.keyResultId as number,
      ]);
    }

    setRows(rows.filter((row) => row.id !== id));
  };
  const initialValues = {
    keyResults: rows.map((row) => ({
      id: row.id,
      keyResult: row.keyResult || "",
      weightage: row.weightage || "",
      selectedKeyResultData: [],
    })),
  };
  const handleConfirmDelete = () => {
    if (pendingDeleteRowId !== null) {
      deleteRow(pendingDeleteRowId);
      setPendingDeleteRowId(null);
      setIsDeleteWarningOpen(false);
    }
  };

  const handleCancelDelete = () => {
    setPendingDeleteRowId(null);
    setIsDeleteWarningOpen(false);
  };

  useEffect(() => {
    fetchInitiatives();
  }, []);

  return (
    <Dialog
      open={isOpenAddPopup}
      className="company-dashboard-add-existing-keyresults-popup-container company-dashboard-edit-key-results-popup-container"
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        onCancel();
      }}
    >
      <DialogTitle id="alert-dialog-title">
        <div className="header-wrapper">
          <div className="header-wrapper-text">Add Existing Key Results</div>
          <div className="image-wrapper">
            <button
              style={{
                border: "none",
                background: "none",
                padding: 0,
              }}
              onClick={onCancel}
            >
              <img
                alt="Close"
                src={CloseIcon}
                className="close-icon"
                style={{ width: "24px", height: "24px", cursor: "pointer" }}
              />
            </button>
          </div>
        </div>
      </DialogTitle>

      <DialogContent>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={() => {
            if (!initiativeAPIData?.id) {
              handleNewCreateKeyResult(
                rows?.map((row) => ({ keyResultId: row?.keyResultId })),
              );
              onCancel();
            }
          }}
        >
          {() => (
            <Form>
              <OverlayScrollbarsComponent
                style={{ maxHeight: "600px" }}
                options={{
                  scrollbars: {
                    theme: "os-theme-dark",
                    autoHide: "scroll",
                    autoHideDelay: 400,
                  },
                }}
              >
                <div className="keyresults-list-container">
                  <div className="keyresults-header">
                    <div className="header-title">Key Result Title</div>
                  </div>

                  {rows.map((row, index) => (
                    <div key={row.id} className="keyresult-row">
                      <div className="keyresult-cell">
                        <button
                          className="select-box"
                          onClick={() => {
                            setSelectedRowIndex(index);
                            setIsOpenAddKeyResultPopup(true);
                          }}
                          type="button"
                        >
                          {row.keyResult || "Select Key Result"}
                        </button>
                      </div>

                      <div className="delete-cell">
                        {row.keyResult && (
                          <button
                            style={{
                              border: "none",
                              background: "none",
                              padding: 0,
                            }}
                            type="button"
                            onClick={() => handleDeleteRow(row.id)}
                          >
                            <img
                              src={DeleteIcon}
                              alt="delete"
                              className="delete-icon"
                            />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {formError && (
                    <div className="error-message">{formError}</div>
                  )}

                  <div className="add-row-icon">
                    <Tooltip
                      title="Click here to add a row"
                      placement="top"
                      arrow
                    >
                      <button
                        style={{
                          border: "none",
                          background: "none",
                          padding: 0,
                        }}
                        type="button"
                        onClick={handleAddRow}
                      >
                        <img
                          src={addCirculIcon}
                          alt="add"
                          className="add-icon"
                          style={{ cursor: "pointer" }}
                        />
                      </button>
                    </Tooltip>
                  </div>
                </div>
                {isOpenAddKeyResultPopup && selectedRowIndex !== null && (
                  <CompanyDashboardAddExistingKeyResultsPopup
                    isOpenAddPopup={isOpenAddKeyResultPopup}
                    onCancel={() => setIsOpenAddKeyResultPopup(false)}
                    setLoading={setLoading}
                    selectedKeyResultsData={getAvailableKeyResults(
                      selectedRowIndex,
                    )}
                    onSelectKeyResult={(selectedKR: any) => {
                      const updatedRows = [...rows];
                      updatedRows[selectedRowIndex].keyResult =
                        selectedKR.keyResultTitle;
                      updatedRows[selectedRowIndex].keyResultId =
                        selectedKR.keyResultId;
                      setRows(updatedRows);
                      setDeletedKeyResultIds((prev) =>
                        prev.filter((id) =>// NOSONAR 
                          id !== selectedKR.keyResultId),
                      );
                      setFormError(null); // Clear error when data is selected
                      setIsOpenAddKeyResultPopup(false);
                      setSelectedRowIndex(null);
                    }}
                  />
                )}
                <DialogActions>
                  <div className="action-btn-wrapper">
                    <button
                      onClick={onCancel}
                      type="button"
                      className="cancel-btn"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      onClick={handleCreate}
                      autoFocus
                      className="action-btn"
                    >
                      Save
                    </button>
                  </div>
                </DialogActions>
              </OverlayScrollbarsComponent>
            </Form>
          )}
        </Formik>
        <WarningConfirmationPopup
          isOpen={isDeleteWarningOpen}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title="De-link Key Result"
          description="You are about to remove the last key result. Are you sure you want to de-link it?"
          confirmButtonText="De-link"
          cancelButtonText="Cancel"
        />
      </DialogContent>
    </Dialog>
  );
};

export default CompanyDashboardAddExistingKeyResultsPopupMainComponent;
